/* eslint-disable no-shadow */
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Form } from 'react-form';

import PropTypes from 'prop-types';

import Popup from '../../../components/Popup';
import { Input, Select } from '../../../components/Form';
import { required } from '../../../utils/validation';
import { SupportLink } from '../../../components/Links';
import styles from '../../../components/PopUps/AddSchoolPopup/addSchoolPopup.scss';
import EditLegacyOfficialFields from './EditLegacyOfficialFields';
import EditOfficialFields from './EditOfficialFields';
import { OfficialShape, ClubOfficialMemberShape } from './type';
import * as drawEditActions from '../../../actions/draw-edit-actions';

const EditOfficialPopupV2 = (props) => {
    const { closePopup, officialToEdit, clubOfficialMembers, 'data-testid': dataTestId } = props;
    const { event } = useSelector(({ drawEdit }) => {
        const { event } = drawEdit;
        return { event };
    });
    const coreUI = useSelector(({ coreUI }) => coreUI);
    const dispatch = useDispatch();

    const {
        eventOfficialRolesTypes,
        // eventOfficialStatusTypes
    } = coreUI.types.tms;

    // Not able to migrate all exisitng TmsEventMember with memberId, have to support legacy official edit.
    const isLegacyOfficialEdit = officialToEdit !== null && officialToEdit.memberId === null;

    const { countries } = coreUI.types.search;
    const [submitting, setSubmitting] = useState(false);
    const [submitSuccessful, setSubmitSuccessful] = useState(false);
    const [updateError, setUpdateError] = useState(false);

    const [selectedOfficial, setSelectedOfficial] = useState(null);

    const handleSubmit = async (values) => {
        const finalValues = {
            ...values,
            ...(selectedOfficial ? {
                memberId: selectedOfficial.memberId,
                firstName: selectedOfficial.firstName,
                lastName: selectedOfficial.lastName,
                countryId: selectedOfficial.countryId,
            } : {}),
        };
        setSubmitting(true);
        setUpdateError(false);
        try {
            if (officialToEdit && officialToEdit.id) {
                await dispatch(drawEditActions.updateEventOfficial(event.data.id, officialToEdit.id, finalValues));
            } else {
                await dispatch(drawEditActions.saveEventOfficial(event.data.id, finalValues));
            }
            setSubmitting(false);
            setSubmitSuccessful(true);
        } catch (error) {
            setSubmitting(false);
            setUpdateError(true);
        }
    };

    return (
        <Popup
            className={`popup-md clear-over-flow`}
            popUpClassName={`${styles.editDualMatchEventPopup}`}
            popUpContentWrapperClassName={`${styles.editDualMatchPopupContent}`}
            onCloseClick={closePopup}
            config={{
                noPadding: false,
                fullScreen: false,
                topGradient: false,
                scrollable: true,
            }}
            data-testid={dataTestId}
        >
            <div className={`${styles.section} pt0`}>
                <h4 data-testid={dataTestId && `${dataTestId}.title`}>{officialToEdit !== null ? 'Edit Official' : 'Add Official'}</h4>
            </div>
            <div className={`${styles.section} pt0 pb24`}>
                <Form onSubmit={values => handleSubmit(values)} id="AddOfficialForm" defaultValues={officialToEdit || {}}>
                    {(formApi) => {
                        const { submitForm, errors /* , values, removeValue, addValue,  setValue, reset */ } = formApi;
                        return (
                            <form onSubmit={submitForm} id="addNewSchool">
                                {!submitSuccessful && (
                                    <>
                                        <div className="row mb16">
                                            <div className={`col-12 mb24`}>
                                                {isLegacyOfficialEdit ? (
                                                    <EditLegacyOfficialFields
                                                        countries={countries}
                                                    />
                                                ) : (
                                                    <EditOfficialFields
                                                        selectedOfficial={selectedOfficial}
                                                        setSelectedOfficial={setSelectedOfficial}
                                                        officialToEdit={officialToEdit}
                                                        countries={countries}
                                                        clubOfficialMembers={clubOfficialMembers}
                                                    />
                                                )}
                                                <Input
                                                    label="Official ID"
                                                    id="itfId"
                                                    field="itfId"
                                                    data-testid={dataTestId && `${dataTestId}.official-id-input`}
                                                />
                                                <Select
                                                    id="role"
                                                    field="role"
                                                    options={eventOfficialRolesTypes}
                                                    validate={required}
                                                    emptyOption="Select Role"
                                                    label={`Role *`}
                                                    attributeToSave="value"
                                                    data-testid={dataTestId && `${dataTestId}.role-select`}
                                                />
                                            </div>
                                        </div>
                                    </>
                                )}
                                {(errors || updateError) && (
                                    <hr />
                                )}
                                {errors && (
                                    <div className="pb24 text-center text-error text-small" data-testid={dataTestId && `${dataTestId}.validation-error`}>Please fill in the required fields.</div>
                                )}
                                {updateError && (
                                    <div className="pb24 text-center text-error text-small" data-testid={dataTestId && `${dataTestId}.update-error`}>
                                        An error occurred while submitting this form. If this problem persists, please contact our <SupportLink target="_blank">support team.</SupportLink>
                                    </div>
                                )}
                                {!submitSuccessful && (
                                    <div className="row">
                                        <div className="col-12 col-md-6 mb16">
                                            <button
                                                onClick={closePopup}
                                                type="button"
                                                className="btn btn-default btn-xl text-uppercase btn-block"
                                                data-testid={dataTestId && `${dataTestId}.cancel-btn`}
                                            >
                                                Cancel
                                            </button>
                                        </div>
                                        <div className="col-12 col-md-6">
                                            <button
                                                type="submit"
                                                className="btn btn-primary btn-xl text-uppercase btn-block"
                                                disabled={submitting || (selectedOfficial === null && officialToEdit === null)}
                                                data-testid={dataTestId && `${dataTestId}.submit-btn`}
                                            >
                                                Submit
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </form>
                        );
                    }}
                </Form>
            </div>
        </Popup>
    );
};

EditOfficialPopupV2.propTypes = {
    closePopup: PropTypes.func.isRequired,
    officialToEdit: PropTypes.shape(OfficialShape),
    clubOfficialMembers: PropTypes.arrayOf(PropTypes.shape(ClubOfficialMemberShape)).isRequired,
    'data-testid': PropTypes.string,
};

EditOfficialPopupV2.defaultProps = {
    officialToEdit: null,
    'data-testid': undefined,
};

export default EditOfficialPopupV2;
